import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IZVOR, IZVORI } from './pretragaKonfig';

/* Glavna pretraga sa mape — jedno polje umesto četiri spiska.

   Stoji na sredini zaglavlja, tamo gde je ranije bio padajući izbor vozila.
   Zamena je namerna, a ne dodatak: izbor vozila je i dalje tu (izvor „Vozilo",
   a prazno polje pod fokusom pokazuje ceo vozni park), pa dva polja koja rade
   isto samo troše širinu zaglavlja koja treba čipovima i alatkama.

   Rezultati su GRUPISANI po izvoru, ne izmešani po nekakvoj oceni. Dispečer
   koji kuca „VS023" zna da traži vozilo; grupa mu daje odgovor bez čitanja
   celog spiska, a boja grupe se poklapa sa bojom koju taj izvor ima na mapi. */

/* Pretraga kreće TEK PREKO tri znaka, dakle od četvrtog.

   Na tri znaka gotovo svaki upit je još uvek početak nečega („BEO", „NOV",
   „MAR"), pa rezultati ionako nisu upotrebljivi — a svaki takav upit je
   potrošen poziv. TomTom Search u besplatnom nivou ima 2 500 poziva mesečno,
   i to je najtesnija kvota koju imamo; tri dispečera koja dnevno traže po
   desetak odredišta već je grebu. Prva tri znaka su najskuplji deo kucanja i
   najmanje vrede. */
const NAJMANJE_ZNAKOVA = 4;
const ODLAGANJE_MS = 350;   // dovoljno da se ne šalje upit po slovu, prekratko da se primeti

/* Vozilo se boji istim pravilom kao na mapi — zeleno vozi, narandžasto stoji,
   sivo se dugo nije javilo. Ista boja na dva mesta znači isto stanje. */
const MINUTA_ZASTARELO = 15;
const bojaVozila = (v) => {
    if ((v.minuta_staro ?? 0) > MINUTA_ZASTARELO) return '#90a4ae';
    return (v.brzina ?? 0) > 3 ? '#2e7d32' : '#ef6c00';
};

const PretragaGlavna = ({
    podesavanja,
    blizu,
    vozila,
    ucitavaMapu,
    onIzbor,
    onOtvoriPodesavanja,
    zovi,
    /* Dok merenje traje, izabrana tačka se ne pinuje nego se DODAJE u merenje.
       Isto polje, drugo značenje — zato natpis mora da se promeni, inače
       operater ne zna zašto mu se pin ne pojavljuje. */
    merenjeAktivno,
}) => {
    const { t, i18n } = useTranslation();

    const [upit, setUpit]         = useState('');
    const [otvoreno, setOtvoreno] = useState(false);
    const [ucitava, setUcitava]   = useState(false);
    const [rezultat, setRezultat] = useState(null);
    const [aktivan, setAktivan]   = useState(0);

    const okvir = useRef(null);
    const polje = useRef(null);
    /* Odgovori umeju da stignu van reda (Nominatim je sporiji od baze). Bez
       brojača bi stariji odgovor pregazio noviji i lista bi pokazivala nešto
       što operater više ne kuca. */
    const brojacUpita = useRef(0);

    const izvori = podesavanja?.izvori || [];

    /* ---- učitavanje ---- */
    useEffect(() => {
        const q = upit.trim();
        if (q.length < NAJMANJE_ZNAKOVA) { setRezultat(null); setUcitava(false); return; }

        const moj = ++brojacUpita.current;
        setUcitava(true);
        const tajmer = setTimeout(async () => {
            try {
                /* Jezik ide do provajdera (TomTom vraca nazive mesta na njemu),
                   a `blizu` naginje pretragu ka onome sto se gleda. Bez toga
                   „Šarčević Co Vrbas" vraca hrvatske firme — provereno. */
                const d = await zovi('/gps/pretraga', {
                    upit: q, izvori, jezik: i18n.language,
                    lat: blizu?.lat, lng: blizu?.lng,
                });
                if (brojacUpita.current !== moj) return;
                setRezultat(d);
                setAktivan(0);
            } catch (e) {
                if (brojacUpita.current === moj) setRezultat(null);
            } finally {
                if (brojacUpita.current === moj) setUcitava(false);
            }
        }, ODLAGANJE_MS);

        return () => clearTimeout(tajmer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [upit, izvori.join(','), zovi, i18n.language, blizu?.lat, blizu?.lng]);

    /* Klik van polja zatvara listu. Zatvara se i pri promeni fokusa tasterom,
       pa se ne oslanja samo na blur — blur na dugmetu u listi bi zatvorio
       listu pre nego što klik stigne da se izvrši. */
    useEffect(() => {
        const vanPolja = (e) => {
            if (okvir.current && !okvir.current.contains(e.target)) setOtvoreno(false);
        };
        document.addEventListener('mousedown', vanPolja);
        return () => document.removeEventListener('mousedown', vanPolja);
    }, []);

    /* ---- grupisanje ----
       Prazan upit pod fokusom pokazuje vozni park: time polje nasleđuje ulogu
       nekadašnjeg padajućeg izbora vozila i ništa se ne gubi zamenom. */
    const grupe = useMemo(() => {
        const q = upit.trim().toLowerCase();

        if (q.length < NAJMANJE_ZNAKOVA) {
            if (!izvori.includes(IZVOR.VOZILO)) return [];
            const spisak = (vozila || [])
                .filter(v => v.vrsta === 'VOZILO')
                .slice(0, 40)
                .map(v => ({
                    izvor: IZVOR.VOZILO,
                    kljuc: `v-${v.mobilisis_id}`,
                    naslov: v.registracija,
                    opis: [v.vozac, v.vreme].filter(Boolean).join(' · '),
                    boja: bojaVozila(v),
                    podatak: v,
                }));
            return spisak.length ? [{ izvor: IZVOR.VOZILO, stavke: spisak, sve: true }] : [];
        }

        if (!rezultat) return [];
        const g = [];

        const vozaci = (rezultat.VozaciDB || []).map(z => ({
            izvor: IZVOR.VOZAC,
            kljuc: `z-${z.id_zaposleni}`,
            naslov: z.ime,
            /* Vozilo je bitnije od telefona: bez njega se „prikaz putanje" i
               „izveštaj" nemaju na šta primeniti, pa stoji prvo. */
            opis: z.registracija
                ? `${z.registracija}${z.telefon ? ' · ' + z.telefon : ''}`
                : (z.telefon || t('gps.pretragaBezVozila')),
            podatak: z,
        }));
        if (vozaci.length) g.push({ izvor: IZVOR.VOZAC, stavke: vozaci });

        const voz = (rezultat.VozilaDB || []).map(v => ({
            izvor: IZVOR.VOZILO,
            kljuc: `v-${v.mobilisis_id}`,
            naslov: v.registracija,
            opis: [[v.marka, v.model].filter(Boolean).join(' ').trim(), v.vozac, v.vreme]
                .filter(Boolean).join(' · '),
            boja: bojaVozila(v),
            podatak: v,
        }));
        if (voz.length) g.push({ izvor: IZVOR.VOZILO, stavke: voz });

        const lok = (rezultat.LokacijeDB || []).map(l => ({
            izvor: IZVOR.LOKACIJA,
            kljuc: `l-${l.izvor}-${l.id}`,
            naslov: l.naziv,
            opis: [l.adresa, l.grad, l.drzava].filter(Boolean).join(', '),
            /* Klijent koji još nije sačuvan kao lokacija nema tačku na mapi —
               oznaka to kaže unapred, da izbor ne izgleda kao da ne radi dok
               se u pozadini traži koordinata. */
            oznaka: l.izvor === 'SACUVANA' ? (l.tip_opis || t('gps.pretragaSacuvana')) : t('gps.pretragaKlijent'),
            zemlja: l.oznaka || null,
            podatak: l,
        }));
        if (lok.length) g.push({ izvor: IZVOR.LOKACIJA, stavke: lok });

        const adr = (rezultat.AdreseDB || []).map((a, i) => ({
            izvor: IZVOR.ADRESA,
            kljuc: `a-${i}`,
            /* display_name ume da bude ceo pasus; prvi deo je ono što je
               operater tražio, ostatak je hijerarhija mesta i ide u opis. */
            naslov: String(a.naziv || '').split(',')[0],
            opis: String(a.naziv || '').split(',').slice(1).join(',').trim(),
            zemlja: a.oznaka || null,
            podatak: a,
        }));
        if (adr.length) g.push({ izvor: IZVOR.ADRESA, stavke: adr });

        return g;
    }, [rezultat, upit, vozila, izvori, t]);

    // Ravan spisak postoji samo zbog tastature — strelica ne zna za grupe.
    const ravno = useMemo(() => grupe.flatMap(g => g.stavke), [grupe]);

    const izaberi = useCallback((stavka) => {
        if (!stavka) return;
        setOtvoreno(false);
        /* Merenje traje kroz više izbora — polje se tada NE prazni, da operater
           može da doda tačku za tačkom bez ponovnog kucanja konteksta. */
        if (!merenjeAktivno) setUpit('');
        onIzbor(stavka.izvor, stavka.podatak);
    }, [onIzbor, merenjeAktivno]);

    const naTaster = (e) => {
        if (e.key === 'Escape') { setOtvoreno(false); polje.current?.blur(); return; }
        if (!otvoreno || !ravno.length) {
            if (e.key === 'ArrowDown') setOtvoreno(true);
            return;
        }
        if (e.key === 'ArrowDown')  { e.preventDefault(); setAktivan(i => (i + 1) % ravno.length); }
        if (e.key === 'ArrowUp')    { e.preventDefault(); setAktivan(i => (i - 1 + ravno.length) % ravno.length); }
        if (e.key === 'Enter')      { e.preventDefault(); izaberi(ravno[aktivan]); }
    };

    const kratak = upit.trim().length > 0 && upit.trim().length < NAJMANJE_ZNAKOVA;
    const prazno = !ucitava && !kratak && otvoreno && upit.trim().length >= NAJMANJE_ZNAKOVA && !ravno.length;

    let redniBroj = -1;   // prolazni indeks kroz sve grupe, da se poklopi sa `ravno`

    return (
        <div className="gps-glavna-pretraga" ref={okvir}>
            <div className={`gps-pp-polje${otvoreno ? ' gps-pp-polje-fokus' : ''}${merenjeAktivno ? ' gps-pp-polje-merenje' : ''}`}>
                <i className={`gps-pp-lupa pi ${ucitava ? 'pi-spin pi-spinner' : 'pi-search'}`} />

                <input
                    ref={polje}
                    type="search"
                    className="gps-pp-unos"
                    value={upit}
                    placeholder={merenjeAktivno ? t('gps.pretragaMerenje') : t('gps.pretragaPlaceholder')}
                    onChange={(e) => { setUpit(e.target.value); setOtvoreno(true); }}
                    onFocus={() => setOtvoreno(true)}
                    onKeyDown={naTaster}
                    aria-label={t('gps.pretragaPlaceholder')}
                />

                {!!upit && (
                    <button
                        type="button"
                        className="gps-pp-brisi"
                        onClick={() => { setUpit(''); setRezultat(null); polje.current?.focus(); }}
                        aria-label={t('gps.pretragaOcisti')}
                    >
                        <i className="pi pi-times" />
                    </button>
                )}
            </div>

            {/* Zupčanik stoji IZVAN polja, kao na velikim mapama: podešavanje
                nije deo upita nego okvir u kom upit radi. */}
            <button
                type="button"
                className="gps-pp-zupcanik"
                onClick={onOtvoriPodesavanja}
                title={t('gps.pretragaPodesavanjaNaslov')}
                aria-label={t('gps.pretragaPodesavanjaNaslov')}
            >
                <i className="pi pi-cog" />
            </button>

            {otvoreno && (grupe.length > 0 || prazno || kratak) && (
                <div className="gps-pp-lista" role="listbox">
                    {kratak && (
                        <div className="gps-pp-poruka">{t('gps.pretragaKratko', { broj: NAJMANJE_ZNAKOVA })}</div>
                    )}

                    {prazno && (
                        <div className="gps-pp-poruka">{t('gps.pretragaNema')}</div>
                    )}

                    {grupe.map(g => {
                        const meta = IZVORI.find(x => x.kljuc === g.izvor);
                        return (
                            <div key={g.izvor} className="gps-pp-grupa">
                                <div className="gps-pp-zaglavlje">
                                    <span className="gps-pp-tacka" style={{ background: meta.boja }} />
                                    {t(meta.naziv)}
                                    {g.sve && <span className="gps-pp-sve">{t('gps.pretragaSviIzFlote')}</span>}
                                </div>

                                {g.stavke.map(s => {
                                    redniBroj += 1;
                                    const moj = redniBroj;
                                    return (
                                        <button
                                            key={s.kljuc}
                                            type="button"
                                            role="option"
                                            aria-selected={moj === aktivan}
                                            className={`gps-pp-red${moj === aktivan ? ' gps-pp-red-aktivan' : ''}`}
                                            style={moj === aktivan ? { '--pp-boja': meta.boja } : undefined}
                                            onMouseEnter={() => setAktivan(moj)}
                                            onClick={() => izaberi(s)}
                                        >
                                            <span className="gps-pp-ikona" style={{ color: s.boja || meta.boja, background: `${meta.boja}14` }}>
                                                <i className={meta.ikona} />
                                            </span>
                                            <span className="gps-pp-tekst">
                                                <span className="gps-pp-naslov">{s.naslov}</span>
                                                {!!s.opis && <span className="gps-pp-opis">{s.opis}</span>}
                                            </span>
                                            {/* Zemlja na KRAJU reda: po njoj se „Vrbas, Srbija" i
                                                „Vrbanja, Hrvatska" razlikuju na prvi pogled, bez
                                                citanja cele adrese. */}
                                            {!!s.zemlja && <span className="gps-pp-zemlja">{s.zemlja}</span>}
                                            {!!s.oznaka && <span className="gps-pp-oznaka">{s.oznaka}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })}

                    {/* Podsetnik na tastaturu stoji na dnu, kao fusnota — pročita
                        se jednom, a lista se gleda svaki put. */}
                    {!!ravno.length && (
                        <div className="gps-pp-podnozje">{t('gps.pretragaPrecice')}</div>
                    )}
                </div>
            )}

            {/* Dok se mapa prvi put puni, polje ostaje upotrebljivo ali se vidi
                da vozila još nisu stigla — inače prazna „Vozilo" grupa deluje
                kao da flota ne postoji. */}
            {ucitavaMapu && !upit && otvoreno && !grupe.length && (
                <div className="gps-pp-lista">
                    <div className="gps-pp-poruka">{t('gps.ucitavanje')}</div>
                </div>
            )}
        </div>
    );
};

export default PretragaGlavna;
